import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import { CheckBoxContainer, Description, OfferTypeListWrapper } from './OfferType.styles';
import CheckboxSelector from '../../CheckboxComponent';
import { selectZipCode } from '../../../reducers/zipCodeReducer';
import FilterSection from '../FilterSection';

interface OfferTypeSelectorTypes {
  filterType: string;
  offerTypeOptions: {
    [key: string]: FilterOption;
  };
  confirmedFilters: FilterTypes;
  availableFilters: Record<string, number>;
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string, isChecked?: boolean) => void;
  handleExposeFilter: (arg: string) => void;
}

const OfferTypeSelector: React.FC<OfferTypeSelectorTypes> = ({
  filterType,
  offerTypeOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
  availableFilters,
}) => {
  const { t } = useTranslation();

  const { confirmedZipcode, market } = useSelector(selectZipCode);
  const isNatPage = confirmedZipcode === '';

  const isCaMarket = market === 'CA';

  const selectedOfferTypesList = useMemo(() => {
    const seletedOfferTypeOptions = confirmedFilters.offerType;
    return seletedOfferTypeOptions.map((offerType) => offerTypeOptions[offerType].label).join(', ');
  }, [confirmedFilters.offerType, offerTypeOptions]);

  const selectedOfferTypes = useMemo(() => {
    return confirmedFilters.offerType;
  }, [confirmedFilters.offerType]);

  const handleAddOfferTypeFilter = (newOfferType: string, isChecked?: boolean) => {
    handleAddFilter(filterType, newOfferType, isChecked);
  };

  const offerTypeList = Object.keys(offerTypeOptions);
  const filteredList = isCaMarket
    ? offerTypeList.filter((toCheck) => toCheck !== 'credit')
    : offerTypeList;

  return (
    <FilterSection
      filterType={filterType}
      selectedFilterLabels={selectedOfferTypesList}
      openedFiltersList={openedFiltersList}
      handleExposeFilter={handleExposeFilter}
    >
      <OfferTypeListWrapper data-testid="offerType-list">
        {filteredList.map((filterOption) => {
          const option = offerTypeOptions[filterOption];
          const isLeaseCheckbox = option.value === 'lease';
          return (
            <CheckBoxContainer key={filterOption}>
              <CheckboxSelector
                inputName={`${filterType}-${option.value}`}
                key={option.value}
                onChange={() =>
                  handleAddOfferTypeFilter(option.value, selectedOfferTypes.includes(option.value))
                }
                value={option.value}
                label={`${option.label} (${availableFilters[option.value] ?? 0})`}
                ariaLabel={option.ariaLabel}
                checked={selectedOfferTypes.includes(option.value)}
                disabled={!availableFilters[option.value]}
                hint={option.hint}
                filterType={filterType}
              />
              {isNatPage && isLeaseCheckbox && !isCaMarket ? (
                <Description disabled={!availableFilters[option.value]}>
                  {t('promo.filters.filterOptions.offerType.noLeaseDescription')}
                </Description>
              ) : null}
            </CheckBoxContainer>
          );
        })}
      </OfferTypeListWrapper>
    </FilterSection>
  );
};

export default OfferTypeSelector;
